/**
 * The platform's fairness words (the rule is in `storefrontCopy/shared.ts`): the fairness page's
 * account of the algorithm, and its verifier's readout of an open (the verdict and every input to
 * the roll) and of a day's seed; and a battle room's account of its rolls, with the labels of
 * their inputs. They state what the code does, so they are the same on every store, never a
 * store's copy, and change together with the algorithm (docs/fairness.md).
 */
import type { OpenRecord } from "./index.js";
import type { DropTableItem, SeedReveal } from "./primitives.js";
import { capitalizeTerm, type StorefrontTerminology } from "./storefrontTerminology.js";

/** What the fairness page says of the mechanism: under its title, and under each tool's heading. */
export const FAIRNESS_EXPLANATION = {
  intro:
    "Server seeds are committed by hash before the day starts and revealed after it ends — any pull can be recomputed in your browser, no trust required.",
  /** The route the verifier reads, and where the roll is recomputed. */
  verify: "GET /v1/opens/:id/verification, recomputed locally with WebCrypto.",
  /** The route the seed lookup reads, and when a seed is committed and revealed. */
  seed: "GET /v1/fairness/seeds/:date — hash-committed up front, revealed after the day ends.",
} as const;

/** How a verification came out: the roll checks out, it does not, or its seed is still secret. */
export type FairnessVerdictStatus = "valid" | "mismatch" | "pending";

export interface FairnessVerdict {
  status: FairnessVerdictStatus;
  label: string;
}

/** One fact of a verification or a seed: a label and its value, mono for a hash, id or number. */
export interface FairnessFactCopy {
  label: string;
  value: string;
  mono?: boolean;
}

/** What the readout states of a verification bundle (`GET /v1/opens/:id/verification`). */
export interface FairnessVerification {
  open: Pick<OpenRecord, "clientSeed" | "nonce" | "result" | "itemIndex"> & {
    item: Pick<DropTableItem, "name">;
  };
  seed: Pick<SeedReveal, "date">;
  salt: string;
}

/** What the browser's recomputation of a bundle found (`verifyOpen`, `@loot-os/client/verify`). */
export interface FairnessRecomputation {
  valid: boolean;
  /** False until the seed's UTC day has ended. */
  seedRevealed: boolean;
  serverSeedMatchesHash: boolean | null;
  computedResult: number | null;
}

/** An open's item, and its index in the drop table the roll chose it from. */
function pullText(name: string, index: number): string {
  return `${name} (index ${index})`;
}

/**
 * A verification's answer. Pending until the seed's UTC day ends, which `valid` is false for too,
 * so an unrevealed seed never reads as a mismatch.
 */
export function fairnessVerdict(
  result: Pick<FairnessRecomputation, "seedRevealed" | "valid">,
): FairnessVerdict {
  if (!result.seedRevealed) {
    return {
      status: "pending",
      label: "Server seed not yet revealed — verify again after the UTC day ends",
    };
  }
  return result.valid
    ? { status: "valid", label: "Verified — the roll checks out" }
    : { status: "mismatch", label: "Mismatch — does not verify" };
}

/**
 * Every input to one open's roll and its recomputation, in order, the item named in the store's
 * term. The labels stay distinct, because a list of facts is keyed by its labels.
 */
export function fairnessVerifyFacts(
  terms: StorefrontTerminology,
  { open, seed, salt }: FairnessVerification,
  {
    computedResult,
    serverSeedMatchesHash,
  }: Pick<FairnessRecomputation, "computedResult" | "serverSeedMatchesHash">,
): FairnessFactCopy[] {
  return [
    { label: capitalizeTerm(terms.item.singular), value: pullText(open.item.name, open.itemIndex) },
    { label: "Recorded roll", value: String(open.result), mono: true },
    {
      label: "Recomputed roll",
      value: computedResult === null ? "—" : String(computedResult),
      mono: true,
    },
    {
      label: "Seed hash matches",
      value: serverSeedMatchesHash === null ? "—" : serverSeedMatchesHash ? "yes" : "NO",
    },
    { label: "Client seed", value: open.clientSeed, mono: true },
    { label: "Salt · nonce", value: `${salt} · ${open.nonce}`, mono: true },
    { label: "Seed date", value: seed.date, mono: true },
  ];
}

/** A day's seed: its sha256 commitment, and the seed once its UTC day has ended. */
export function fairnessSeedFacts(seed: Pick<SeedReveal, "seedHash" | "seed">): FairnessFactCopy[] {
  return [
    { label: "Commitment (sha256)", value: seed.seedHash, mono: true },
    {
      label: "Server seed",
      value: seed.seed ?? "not yet revealed — check back after the UTC day ends",
      // Until then the value is a sentence, not a seed to compare.
      mono: !!seed.seed,
    },
  ];
}

/** What a battle's room states of its rolls, and the labels of everything that goes into one. */
export interface BattleFairnessReadout {
  /** How every pull is rolled, in the store's word for a battle, and when the seed is revealed. */
  description: string;
  /** The inputs every open shares: the battle's salt, and its day's seed. */
  facts: { salt: string; seedDate: string; seedHash: string; seed: string };
  /** In place of the seed's day before the battle starts. */
  notStarted: string;
  /** In place of the seed's commitment and the seed while they load. */
  loading: string;
  /** In place of the seed until its UTC day ends. */
  seedHidden: string;
  /** The opens table's columns, the item in the store's term. */
  headers: {
    round: string;
    seat: string;
    clientSeed: string;
    nonce: string;
    result: string;
    item: string;
  };
  /** A seat's client seed when it has none. */
  noClientSeed: string;
  /** An open's item, and its index in the drop table. */
  pull: (name: string, index: number) => string;
}

/** A battle room's fairness readout, in the store's terms. */
export function battleFairnessReadout(terms: StorefrontTerminology): BattleFairnessReadout {
  const { battle, item } = terms;
  return {
    description: `Every pull is HMAC-SHA512(server seed, client seed + ${battle.singular} salt + nonce). The server seed is revealed after its UTC day ends.`,
    facts: {
      salt: `${capitalizeTerm(battle.singular)} salt`,
      seedDate: "Seed date (UTC)",
      seedHash: "Server seed hash",
      seed: "Server seed",
    },
    notStarted: "not started",
    loading: "…",
    seedHidden: "revealed after the UTC day ends",
    headers: {
      round: "Round",
      seat: "Seat",
      clientSeed: "Client seed",
      nonce: "Nonce",
      result: "Result",
      item: capitalizeTerm(item.singular),
    },
    noClientSeed: "—",
    pull: pullText,
  };
}
